import {
  formatZodError,
  scheduleMembershipsForTable,
  type ScheduleMembership,
} from "@Simplicity-Education/core";
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  sonnerToast,
} from "@simplicity/ui";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ApiError, apiFetch, hasIssues } from "../../api";
import type { AdhocTransformRun, ProjectDetailResponse } from "../../configTypes";
import { isTerminalAdhocStatus, useAdhocRunPolling } from "../../hooks/useAdhocRunPolling";
import {
  bronzeOrSilverPrefix,
  bronzeOrSilverSide,
  findConnectorsForPair,
  tableBaseName,
  type BuildPipelineGraphInput,
  type Wave,
} from "../../lib/buildPipelineGraph";
import type { TableSelection } from "./PipelineDag";
import { WAVE_LABELS } from "./pipelineDagBuilder";
import { SyncScheduleChoice } from "./SyncScheduleChoice";

type SnapshotResponse = BuildPipelineGraphInput["snapshot"];

interface PipelineDrawerProps {
  selection: TableSelection | null;
  snapshot: SnapshotResponse;
  projectDetail: ProjectDetailResponse;
  onClose: () => void;
}

function formatBqTimestamp(epochMsString: string | null | undefined): string {
  if (epochMsString == null) return "—";
  const ms = Number(epochMsString);
  if (!Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleString();
}

function EditButton({
  path,
  navigate,
  onClose,
}: {
  path: string;
  navigate: ReturnType<typeof useNavigate>;
  onClose: () => void;
}) {
  return (
    <Button
      variant="outline"
      onClick={() => {
        void navigate(path);
        onClose();
      }}
    >
      Edit
    </Button>
  );
}

const QUEUE_FAILED = "Failed to queue cascade";

/** Reads the parsed body, not the message, so a JSON body with no message or no readable
 *  issues still falls back to QUEUE_FAILED. */
function queueErrorMessage(err: unknown): string {
  const body = err instanceof ApiError ? err.body : null;
  if (body != null && !hasIssues(body) && typeof body.message === "string") return body.message;
  return formatZodError(body ?? err, { unknownFallback: QUEUE_FAILED });
}

/**
 * SIM-1189: one AdhocTransformRun row whose `runPlan` carries N steps; status
 * goes terminal only once every step finishes. No history read-back — cascade
 * rows store `transformDefinitionId: null`, so nothing can key on them yet
 * (SIM-3311 slice 2).
 */
function useAdhocCascadeRun(args: { connectorId: string; table: string; schedule?: string }) {
  const polling = useAdhocRunPolling();

  async function trigger() {
    const { connectorId, table, schedule } = args;
    try {
      const run = await apiFetch<AdhocTransformRun>("/api/adhoc-transform-runs", {
        method: "POST",
        body: JSON.stringify({
          cascadeFromTable: { connectorId, table, ...(schedule ? { schedule } : {}) },
        }),
      });
      polling.adoptTriggeredRun(run);
    } catch (err) {
      sonnerToast.error(QUEUE_FAILED, { description: queueErrorMessage(err) });
    }
  }

  return { ...polling, trigger };
}

function AdhocCascadeStatus({
  run,
  lostContact,
}: {
  run: AdhocTransformRun;
  lostContact: boolean;
}) {
  if (lostContact && !isTerminalAdhocStatus(run.status)) {
    return (
      <p className="mt-2 text-sm text-destructive">
        Lost contact with this cascade — it is still executing. Reload to check the result.
      </p>
    );
  }
  if (run.status === "pending") {
    return <p className="mt-2 text-sm text-muted-foreground">Queued — waiting for runner...</p>;
  }
  if (run.status === "claimed") {
    return <p className="mt-2 text-sm text-muted-foreground">Running cascade...</p>;
  }
  if (run.status === "completed") {
    return <p className="mt-2 text-sm text-success">Cascade completed.</p>;
  }
  if (run.status === "failed") {
    return (
      <p className="mt-2 text-sm text-destructive">Failed: {run.errorMessage ?? "Unknown error"}</p>
    );
  }
  return null;
}

/**
 * SIM-2273: the schedule this sync runs as. A sole schedule is used without asking; with several,
 * only an explicit pick counts. Multi-instance schedules are never a valid answer.
 */
function resolveSyncSchedule(
  memberships: ScheduleMembership[],
  picked: string | undefined,
): string | undefined {
  const candidate = memberships.length === 1 ? memberships[0] : undefined;
  const chosen = candidate ?? memberships.find((m) => m.schedule === picked);
  return chosen?.instances === 1 ? chosen.schedule : undefined;
}

function CascadeButton({
  connectorId,
  table,
  vars,
}: {
  connectorId: string;
  table: string;
  vars: Record<string, unknown>;
}) {
  const memberships = scheduleMembershipsForTable(vars, table);
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const schedule = resolveSyncSchedule(memberships, picked);
  const needsSchedule = memberships.length > 0 && schedule === undefined;
  const { latestRun, isRunning, lostContact, trigger } = useAdhocCascadeRun({
    connectorId,
    table,
    schedule,
  });
  const label = schedule ? `Sync now + cascade (${schedule})` : "Sync now + cascade";
  return (
    <div className="flex flex-col gap-2">
      <SyncScheduleChoice memberships={memberships} value={picked} onChange={setPicked} />
      <Button
        variant="outline"
        onClick={() => void trigger()}
        disabled={isRunning || needsSchedule}
        data-testid="pipeline-cascade-button"
      >
        {isRunning ? "Running..." : label}
      </Button>
      {latestRun != null ? <AdhocCascadeStatus run={latestRun} lostContact={lostContact} /> : null}
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 last:border-0">
      <dt className="shrink-0 text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{value}</dd>
    </div>
  );
}

function FieldList({ children }: { children: React.ReactNode }) {
  return <dl className="mt-2">{children}</dl>;
}

function NoMatch({ wave, entityId }: { wave: Wave; entityId: string }) {
  return (
    <p className="text-sm text-muted-foreground">
      Could not resolve {wave} entity <code className="font-mono text-xs">{entityId}</code>.
    </p>
  );
}

interface DrawerBody {
  title: string;
  description: string;
  body: React.ReactNode;
  editPath?: string;
  /**
   * SIM-1189 PR B2: when set, render a "Sync now + cascade" button in the
   * footer. Only populated for bronze-silver-latest nodes whose ownership
   * resolves to a single connector (otherwise we'd ambiguously target the
   * sync).
   */
  cascadeTarget?: { connectorId: string; table: string; vars: Record<string, unknown> };
}

function buildConnectorBody(
  entityId: string,
  projectDetail: ProjectDetailResponse,
): DrawerBody | null {
  const connector = projectDetail.connectors.find((c) => c.id === entityId);
  if (connector == null) return null;
  const schedules = (connector.vars as { schedules?: Record<string, unknown> }).schedules ?? {};
  const scheduleNames = Object.keys(schedules);
  const baseUrl =
    typeof (connector.vars as { baseUrl?: unknown }).baseUrl === "string"
      ? (connector.vars as { baseUrl: string }).baseUrl
      : "—";

  return {
    title: connector.name,
    description: "Connector",
    body: (
      <FieldList>
        <FieldRow label="Type" value={connector.type} />
        <FieldRow label="Prefix" value={connector.prefix} />
        <FieldRow label="Base URL" value={baseUrl} />
        <FieldRow
          label="Schedules"
          value={scheduleNames.length > 0 ? scheduleNames.join(", ") : "—"}
        />
      </FieldList>
    ),
    editPath: `/config/connectors/${connector.id}`,
  };
}

interface PairSideMatch {
  datasetId: string;
  tableId: string;
  numRows: number | null;
  lastModifiedTime: string | null;
}

function findPairSides(
  prefix: string,
  baseName: string,
  snapshot: SnapshotResponse,
): { bronze: PairSideMatch | null; silverLatest: PairSideMatch | null } {
  let bronze: PairSideMatch | null = null;
  let silverLatest: PairSideMatch | null = null;
  for (const dataset of snapshot.datasets) {
    if (bronzeOrSilverPrefix(dataset.datasetId) !== prefix) continue;
    const side = bronzeOrSilverSide(dataset.datasetId);
    if (side == null) continue;
    for (const table of dataset.tables) {
      if (tableBaseName(table.tableId) !== baseName) continue;
      const match: PairSideMatch = {
        datasetId: dataset.datasetId,
        tableId: table.tableId,
        numRows: table.numRows ?? null,
        lastModifiedTime: table.lastModifiedTime ?? null,
      };
      if (side === "bronze" && bronze == null) bronze = match;
      if (side === "silver" && silverLatest == null) silverLatest = match;
    }
  }
  return { bronze, silverLatest };
}

function fqTableName(side: PairSideMatch | null): React.ReactNode {
  if (side == null) return "—";
  return (
    <code className="font-mono text-xs">
      {side.datasetId}.{side.tableId}
    </code>
  );
}

function formatNumOrDash(n: number | null | undefined): string {
  return n != null ? n.toLocaleString() : "—";
}

function formatOwningConnectors(
  owningConnectors: { id: string; prefix: string }[],
  projectDetail: ProjectDetailResponse,
): string {
  if (owningConnectors.length === 0) return "—";
  return owningConnectors
    .map((c) => projectDetail.connectors.find((pc) => pc.id === c.id)?.name ?? c.prefix)
    .join(", ");
}

function buildPairFieldList(
  bronze: PairSideMatch | null,
  silverLatest: PairSideMatch | null,
  owningConnectors: { id: string; prefix: string }[],
  projectDetail: ProjectDetailResponse,
): React.ReactNode {
  const owningLabel = owningConnectors.length > 1 ? "Owning connectors" : "Owning connector";
  return (
    <FieldList>
      <FieldRow label="Bronze table" value={fqTableName(bronze)} />
      <FieldRow label="Silver-latest table" value={fqTableName(silverLatest)} />
      <FieldRow label="Snapshots" value={formatNumOrDash(bronze?.numRows)} />
      <FieldRow label="Current rows" value={formatNumOrDash(silverLatest?.numRows)} />
      <FieldRow
        label="Silver-latest last updated"
        value={formatBqTimestamp(silverLatest?.lastModifiedTime)}
      />
      <FieldRow label="Bronze last updated" value={formatBqTimestamp(bronze?.lastModifiedTime)} />
      <FieldRow
        label={owningLabel}
        value={formatOwningConnectors(owningConnectors, projectDetail)}
      />
    </FieldList>
  );
}

function buildBronzeSilverLatestBody(
  entityId: string,
  snapshot: SnapshotResponse,
  projectDetail: ProjectDetailResponse,
): DrawerBody | null {
  const [prefix, baseName] = entityId.split("|");
  if (prefix == null || baseName == null) return null;
  const { bronze, silverLatest } = findPairSides(prefix, baseName, snapshot);
  if (bronze == null && silverLatest == null) return null;
  // Round 6: dataset-ownership-primary lookup. See findOwningConnectorIds.
  const owningConnectors = findConnectorsForPair(
    [bronze?.datasetId, silverLatest?.datasetId],
    baseName,
    projectDetail.connectors,
  );
  const editPath =
    owningConnectors[0] != null ? `/config/connectors/${owningConnectors[0].id}` : undefined;
  // SIM-1189 PR B2: only offer cascade when ownership is unambiguous. A pair
  // owned by multiple connectors (rare; would need explicit table selection)
  // is left without a button to avoid syncing the wrong source.
  const owner = owningConnectors.length === 1 ? owningConnectors[0] : undefined;
  const cascadeTarget =
    owner != null
      ? {
          connectorId: owner.id,
          table: baseName,
          vars: projectDetail.connectors.find((c) => c.id === owner.id)?.vars ?? {},
        }
      : undefined;
  return {
    title: baseName,
    description: `Bronze snapshot + Silver-latest pair • prefix \`${prefix}\``,
    body: buildPairFieldList(bronze, silverLatest, owningConnectors, projectDetail),
    editPath,
    cascadeTarget,
  };
}

function buildTransformBody(
  entityId: string,
  projectDetail: ProjectDetailResponse,
  wave: Wave,
): DrawerBody | null {
  const td = projectDetail.transformDefinitions.find((t) => t.id === entityId);
  if (td == null) return null;
  const sqlPreview =
    td.sqlTemplate.length > 240 ? `${td.sqlTemplate.slice(0, 240)}…` : td.sqlTemplate;

  return {
    title: td.name,
    description: `${WAVE_LABELS[wave]} transform`,
    body: (
      <>
        <FieldList>
          <FieldRow label="Output" value={`${td.outputDataset}.${td.outputTable}`} />
          <FieldRow label="Enabled" value={td.enabled ? "Yes" : "No"} />
          <FieldRow
            label="Depends on"
            value={
              td.dependencies.length === 0
                ? "—"
                : td.dependencies.map((d) => `${d.sourceDataset}.${d.sourceTable}`).join(", ")
            }
          />
        </FieldList>
        <div className="mt-4">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground">SQL preview</h3>
          <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded border border-border bg-muted/40 p-2 font-mono text-[11px] text-foreground">
            {sqlPreview}
          </pre>
        </div>
      </>
    ),
    editPath: `/config/transform-definitions/${td.id}`,
  };
}

function buildArchiveBody(
  entityId: string,
  projectDetail: ProjectDetailResponse,
): DrawerBody | null {
  const ed = projectDetail.exportDefinitions.find((e) => e.id === entityId);
  if (ed == null) return null;
  const latest = ed.latestRun;

  return {
    title: ed.name,
    description: "Archive export",
    body: (
      <FieldList>
        <FieldRow label="Cadence" value={ed.cadence} />
        <FieldRow label="Format" value={ed.format} />
        <FieldRow label="Filename" value={ed.filenameTemplate} />
        <FieldRow label="Enabled" value={ed.enabled ? "Yes" : "No"} />
        <FieldRow label="Last run" value={latest != null ? latest.status : "—"} />
      </FieldList>
    ),
  };
}

function resolveDrawer(
  selection: TableSelection,
  snapshot: SnapshotResponse,
  projectDetail: ProjectDetailResponse,
): DrawerBody | null {
  switch (selection.wave) {
    case "connector":
      return buildConnectorBody(selection.entityId, projectDetail);
    case "bronze-silver-latest":
      return buildBronzeSilverLatestBody(selection.entityId, snapshot, projectDetail);
    case "silver":
    case "gold":
    case "diamond":
      return buildTransformBody(selection.entityId, projectDetail, selection.wave);
    case "archive":
      return buildArchiveBody(selection.entityId, projectDetail);
  }
}

export function PipelineDrawer({
  selection,
  snapshot,
  projectDetail,
  onClose,
}: PipelineDrawerProps) {
  const navigate = useNavigate();
  const resolved = selection != null ? resolveDrawer(selection, snapshot, projectDetail) : null;
  const open = selection != null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md" aria-describedby={undefined}>
        {selection == null ? null : resolved == null ? (
          <>
            <SheetHeader>
              <SheetTitle>Unknown entity</SheetTitle>
              <SheetDescription>Pipeline inspector</SheetDescription>
            </SheetHeader>
            <NoMatch wave={selection.wave} entityId={selection.entityId} />
          </>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>{resolved.title}</SheetTitle>
              <SheetDescription>{resolved.description}</SheetDescription>
            </SheetHeader>
            <div className="mt-4 overflow-y-auto pr-1" data-testid="pipeline-drawer-body">
              {resolved.body}
            </div>
            <SheetFooter className="mt-6 flex-col items-stretch gap-2 sm:flex-col sm:items-stretch sm:gap-2">
              {resolved.cascadeTarget != null ? (
                <CascadeButton
                  connectorId={resolved.cascadeTarget.connectorId}
                  table={resolved.cascadeTarget.table}
                  vars={resolved.cascadeTarget.vars}
                />
              ) : null}
              {resolved.editPath != null ? (
                <EditButton path={resolved.editPath} navigate={navigate} onClose={onClose} />
              ) : null}
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
